import { UiIconButtonComponent } from '@/ui/icon-button/ui-icon-button';
import { frenchSort } from '@/utils/toolkit/array/sort.utils';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe, NgClass } from '@angular/common';
import { Component, inject } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { isEqual } from 'lodash-es';
import type moment from 'moment';
import { combineLatest, of } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { LAST_TWELVE_MONTHS_PERIOD } from '../../../constants/period.constant';
import { AdvizeoTooltipDirective } from '../../../directives/advizeo-tooltip/advizeo-tooltip.directive';
import type { BuildingId, Period } from '../../../interfaces/Building.interface';
import type { TileConfig, TileData } from '../../../interfaces/Tile.interface';
import type { IpeTilesSettings, IpeTilesTranslationKeys } from '../../../interfaces/Widget.interface';
import {
    type IpeTileCreationOutput,
    IpeTileCreationPopover,
    type IpeTileCreationPopoverParams
} from '../../../pages/ipe-tile-creation/ipe-tile-creation';
import { DateFormatPipe } from '../../../pipes/date-format/date-format';
import { TranslatePeriodPipe } from '../../../pipes/translate-period.pipe';
import { VatModes } from '../../../providers/budgets/budgets.constants';
import { DashboardingProvider } from '../../../providers/dashboarding.provider';
import { ProjectsProvider } from '../../../providers/projects/projects.provider';
import { TileConstants } from '../../../providers/tiles/tiles.constants';
import { TilesProvider } from '../../../providers/tiles/tiles.provider';
import { WidgetSize } from '../../../providers/widgets.contants';
import { AdvizeoModalService } from '../../../services/advizeo-modal.service';
import { DeveloperHelperComponent } from '../../developer-helper/developer-helper';
import { HelpButtonComponent } from '../../help-button/help-button';
import { TileSpawnerComponent } from '../../tile/tile-spawner/tile-spawner';
import { AbstractWidgetClass } from '../AbstractWidget.class';
import { OldDataSourceCode } from './../../../services/developer-helper.service';

@UntilDestroy()
@Component({
    styleUrls: ['./widget-ipe-tiles.ng-cli.scss'],
    selector: 'widget-ipe-tiles',
    templateUrl: 'widget-ipe-tiles.html',
    imports: [
        DeveloperHelperComponent,
        HelpButtonComponent,
        UiIconButtonComponent,
        AdvizeoTooltipDirective,
        NgClass,
        TileSpawnerComponent,
        AsyncPipe,
        DateFormatPipe,
        TranslatePipe,
        TranslatePeriodPipe
    ]
})
export class WidgetIpeTilesComponent extends AbstractWidgetClass<IpeTilesTranslationKeys, IpeTilesSettings> {
    private _tilesProvider = inject(TilesProvider);
    private _dashboardingProvider = inject(DashboardingProvider);
    private _translateService = inject(TranslateService);
    private _projectsProvider = inject(ProjectsProvider);
    private _advizeoModalService = inject(AdvizeoModalService);

    size = WidgetSize.FULL;

    tilesData: TileData[] = [];

    dateStart!: moment.Moment;
    dateEnd!: moment.Moment;
    dateFormat!: string;
    period!: Period;
    tilesConfig: TileConfig = {};

    buildingId!: BuildingId;
    helpButtonUrl = 'tuiles-indice-performance-energetique';

    lastTwelveMonths = LAST_TWELVE_MONTHS_PERIOD;

    vatMode!: VatModes;

    OldDataSourceCode = OldDataSourceCode;

    ngOnInit() {
        this.dateFormat = this._translateService.instant('DATE_FORMAT.DEFAULT');

        combineLatest(
            this._dashboardingProvider.getMeaningfulBuildingId$(),
            (this.config && this.config.ignorePeriodsStore
                ? of(this._dashboardingProvider.generateLast12MonthsPeriod())
                : this._dashboardingProvider.getSelectedPeriod$().pipe(filter(isNotNullish), distinctUntilChanged<Period>(isEqual))
            ).pipe(
                map(period => {
                    if (!period) return;
                    this.period = period;
                    return this._dashboardingProvider.normalizePeriodTo12FullMonths(period);
                })
            ),
            this._projectsProvider.getVatMode$()
        )
            .pipe(
                filter(([buildingId, period, vatMode]) => !!buildingId && !!period && !!vatMode),
                untilDestroyed(this)
            )
            .subscribe(([buildingId, period, vatMode]) => {
                this.dateStart = period!.start;
                this.dateEnd = period!.end;
                this.tilesConfig.period = period;
                this.buildingId = buildingId;
                this.tilesConfig.hasTileAdminAccess$ = this._tilesProvider.hasTileAdminOption$(this.buildingId);
                this.vatMode = vatMode!;
                this.fetchTiles();
            });
    }

    fetchTiles() {
        this._tilesProvider
            .fetchBuildingTilesByGroup(this.buildingId, this.config.zone, this.dateStart, this.dateEnd, this.vatMode === VatModes.VAT_INCL)
            .then((tilesData: TileData[]) => {
                this.tilesData = tilesData.sort((a, b) => frenchSort(a.name, b.name)).sort((a, b) => frenchSort(a.tileType, b.tileType));
            })
            .catch(err => console.log(err));
    }

    newTile() {
        const params: IpeTileCreationPopoverParams = {
            dateStart: this.dateStart,
            dateEnd: this.dateEnd,
            zone: this.config.zone,
            buildingId: this.buildingId,
            editMode: false
        };
        const popover = this._advizeoModalService.openModal(IpeTileCreationPopover, params);

        // Reload widgets
        popover.afterClosed().then((data: IpeTileCreationOutput | undefined) => {
            if (data && data.tile) {
                this.tilesData.push(data.tile);
                this.tilesData.sort((a, b) => frenchSort(a.name, b.name)).sort((a, b) => frenchSort(a.tileType, b.tileType));
            }
        });
    }

    getContainerClassForTileType(tileType: string) {
        switch (tileType) {
            case TileConstants.IPE_VALUE:
            case TileConstants.IPE_VALUE_VARIABLE_INDICATOR: {
                return { 'item-value': true };
            }

            case TileConstants.IPE_GAUGE:
            case TileConstants.IPE_GAUGE_VARIABLE_INDICATOR: {
                return { 'item-gauge': true };
            }
        }
    }

    onTileDelete(index: number) {
        this.tilesData.splice(index, 1);
    }

    onTileEdit(index: number, tileData: TileData) {
        console.log('onTileEdit', index, tileData);
        this.tilesData[index] = tileData;
    }
}
